import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCT_VARIANT } from '../types/product-variant.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductVariant (revised per 0015).
 * Stored in 'org/{orgId}/product-variant'. The doc id equals the variantKey.
 *
 * Overrides are **no longer stored** on the variant. The variant identity is now
 * based on: productId + technicalSheetId (optional) + colorId + variationOptionId1..5.
 */
export const productVariantDescriptor = {
    name: PRODUCT_VARIANT,
    label: 'Variante de Produto',
    domain: 'catalog',
    collectionPath: 'org/{orgId}/product-variant',
    pathPatternKeys: { orgId: 'string' },
    version: 3,
    description: 'Identidade de variante: produto + ficha técnica (opcional) + valores de variação (cor, tamanho, opções)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        { name: 'productId', label: 'Produto', type: 'string', required: true },
        { name: 'technicalSheetId', label: 'Ficha técnica', type: 'string', required: false },
        { name: 'colorId', label: 'Cor', type: 'string', required: false },
        { name: 'sizeOccurrenceId', label: 'Tamanho', type: 'string', required: false },
        { name: 'sizeGridId', label: 'Grade de tamanhos', type: 'string', required: false },
        { name: 'parentVariantId', label: 'Variante parcial', type: 'string', required: false },
        { name: 'variationOptionId1', label: 'Opção 1', type: 'string', required: false },
        { name: 'variationOptionId2', label: 'Opção 2', type: 'string', required: false },
        { name: 'variationOptionId3', label: 'Opção 3', type: 'string', required: false },
        { name: 'variationOptionId4', label: 'Opção 4', type: 'string', required: false },
        { name: 'variationOptionId5', label: 'Opção 5', type: 'string', required: false },
        { name: 'variantKey', example: '8842-VM01', label: 'Chave', type: 'string', required: true },
        { name: 'description', example: 'Malha PV 30/1 Penteada - Vermelho Carmim', label: 'Descrição', type: 'string', required: false, maxLength: FIELD_LIMITS.NAME },
        { name: 'active', label: 'Ativa', type: 'boolean', required: false },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto base da variante',
        },
        {
            fieldName: 'technicalSheetId',
            targetEntity: 'TechnicalSheet',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Ficha técnica base da variante (opcional — ausente = variante sem receita)',
        },
        {
            fieldName: 'colorId',
            targetEntity: 'Color',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Valor do eixo COLOR',
        },
        {
            fieldName: 'sizeOccurrenceId',
            targetEntity: 'SizeOccurrence',
            relationship: 'many-to-one',
            displayField: 'label',
            description: 'Tamanho (eixo SIZE)',
        },
        {
            fieldName: 'sizeGridId',
            targetEntity: 'SizeGrid',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Grade da ocorrência de tamanho (endereço da subcoleção; não entra no variantKey)',
        },
        {
            fieldName: 'parentVariantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante parcial (identificadores); null quando esta já é parcial',
        },
        {
            fieldName: 'variationOptionId1',
            targetEntity: 'VariationOption',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Valor do eixo OPTION (slot 1)',
        },
        {
            fieldName: 'variationOptionId2',
            targetEntity: 'VariationOption',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Valor do eixo OPTION (slot 2)',
        },
        {
            fieldName: 'variationOptionId3',
            targetEntity: 'VariationOption',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Valor do eixo OPTION (slot 3)',
        },
        {
            fieldName: 'variationOptionId4',
            targetEntity: 'VariationOption',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Valor do eixo OPTION (slot 4)',
        },
        {
            fieldName: 'variationOptionId5',
            targetEntity: 'VariationOption',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Valor do eixo OPTION (slot 5)',
        },
    ],
};
//# sourceMappingURL=product-variant.descriptor.js.map